import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { SEO } from '../components/SEO';
import { 
  Clock, 
  CheckCircle2, 
  MapPin, 
  Phone, 
  Smartphone,
  Mail,
  Bus,
  Quote,
  Package,
  Eye,
  Camera,
  Building2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Paket-Definitionen mit detaillierten Beschreibungen und PayPal-Button-IDs
const PACKAGES = [
  {
    id: 'grundkurs',
    name: 'BOTTROP: Erste-Hilfe-Kurs (9 UE)',
    price: 35.00,
    priceLabel: '35 €',
    shortDesc: 'Behördlich anerkannter Erste-Hilfe-Kurs für den Führerschein, Arbeit und Ausbildung (Dauer: 7,5 Std.)',
    details: [
      'Für Fahrschüler aller Führerscheinklassen – auch für LKW & BUS',
      'Für Ersthelfer, Betriebe, Unternehmen, Studenten, Auszubildende, Trainer'
    ],
    badge: 'Bundesweit zugelassen',
    icon: Package,
    popular: false,
    paypalButtonId: '3AP3B2ZKV9R6A'
  },
  {
    id: 'sehtest',
    name: 'BOTTROP: Erste-Hilfe + Sehtest',
    price: 37.50,
    priceLabel: '37,50 €',
    shortDesc: 'Behördlich anerkannter Erste-Hilfe-Kurs + amtlicher Sehtest (Auto + Motorrad) (Dauer: 7,5 Std.)',
    details: [
      'Für Fahrschüler aller Führerscheinklassen – auch für LKW & BUS',
      'Für Ersthelfer, Betriebe, Unternehmen, Studenten, Auszubildende, Trainer'
    ],
    badge: 'Bundesweit anerkannt',
    icon: Eye,
    popular: false,
    paypalButtonId: 'JZSUEFEWWLAFA'
  },
  {
    id: 'passbilder',
    name: 'BOTTROP: Erste-Hilfe + 8 Passbilder',
    price: 37.50,
    priceLabel: '37,50 €',
    shortDesc: 'Behördlich anerkannter Erste-Hilfe-Kurs + biometrische Passbilder für den Führerschein (Dauer: 7,5 Std.)',
    details: [
      'Für Fahrschüler aller Führerscheinklassen – auch für LKW & BUS',
      'Für Ersthelfer, Betriebe, Unternehmen, Studenten, Auszubildende, Trainer'
    ],
    badge: 'Bundesweit anerkannt',
    icon: Camera,
    popular: false,
    paypalButtonId: 'LZRPBP7MFJWNL'
  },
  {
    id: 'komplett',
    name: 'BOTTROP: Komplettpaket',
    price: 40.00,
    priceLabel: '40 €',
    shortDesc: 'Behördlich anerkannter Erste-Hilfe-Kurs + amtl. Sehtest + 8 biometrische Passbilder (Dauer: 7,5 Std.)',
    details: [
      'Für Fahrschüler aller Führerscheinklassen – auch für LKW & BUS',
      'Für Ersthelfer, Betriebe, Unternehmen, Studenten, Auszubildende, Trainer'
    ],
    badge: 'Bundesweit anerkannt',
    icon: CheckCircle2,
    popular: false,
    paypalButtonId: '3VW9LNFN7JKZ4'
  },
  {
    id: 'bg',
    name: 'BOTTROP: Erste-Hilfe-Kurs für Betriebe (BG/DGUV)',
    price: 0,
    priceLabel: 'Kostenfrei',
    shortDesc: 'Teilnahme über Berufsgenossenschaft / Unfallkasse. Abrechnungsformular ausgefüllt, gestempelt und unterschrieben zum Kurstag mitbringen.',
    details: [
      'Für betriebliche Ersthelfer nach DGUV-Vorschrift 1',
      'Abrechnung direkt über Ihre Berufsgenossenschaft'
    ],
    badge: 'Bei BG-Kostenübernahme',
    icon: Building2,
    popular: false,
    isBG: true
  }
];

// Helper-Funktion für Paket-Namen mit roten Hervorhebungen
const formatPackageName = (pkgId, name) => {
  const cleanName = name.replace('BOTTROP: ', '');
  
  switch(pkgId) {
    case 'sehtest':
      // "Erste-Hilfe + Sehtest" → nur "Sehtest" rot
      return <>Erste-Hilfe + <span className="text-red-600">Sehtest</span></>;
    case 'passbilder':
      // "Erste-Hilfe + 8 Passbilder" → nur "8 Passbilder" rot
      return <>Erste-Hilfe + <span className="text-red-600">8 Passbilder</span></>;
    case 'komplett':
      // "Komplettpaket" → nur "Komplettpaket" rot
      return <span className="text-red-600">Komplettpaket</span>;
    case 'bg':
      // BG-Kurs - normaler Name
      return cleanName;
    default:
      return cleanName;
  }
};

const HomePage = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Ref für Video-Start
  const desktopVideoRef = useRef(null);

  // Video sofort starten sobald DOM geladen
  useEffect(() => {
    const playVideo = () => {
      if (desktopVideoRef.current) {
        desktopVideoRef.current.play().catch(() => {});
      }
    };
    
    // Sofort versuchen zu starten
    playVideo();
    
    // Auch bei Sichtbarkeit starten (falls Tab gewechselt wurde)
    const handleVisibility = () => {
      if (!document.hidden) playVideo();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, faqsRes] = await Promise.all([
          axios.get(`${API_URL}/api/reviews`),
          axios.get(`${API_URL}/api/faq`)
        ]);
        setReviews(reviewsRes.data);
        setFaqs(faqsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const learningContent = [
    "Richtiges Verhalten im Notfall",
    "Maßnahmen bei Bewusstlosigkeit",
    "Hilfe bei Atemstillstand",
    "Erste Hilfe bei Herzinfarkt",
    "Versorgung äußerer Verletzungen",
    "und vieles mehr"
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO page="home" />
      <Navbar />
      <main className="flex-1">
        
        {/* HERO MIT PAKETEN - Symmetrisches Layout: Links Pakete | Mitte Video | Rechts Pakete */}
        <section id="paket-auswahl" className="bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
          {/* Volle Breite - Bündig mit Navbar (minimaler Abstand oben) */}
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-4 md:pt-2 md:pb-6">
            
            {/* Headline - Klar und prägnant */}
            <div className="text-center mb-3 lg:mb-4">
              <h1 className="text-xl sm:text-2xl lg:text-2xl font-bold text-[#1e3a5f] leading-tight">
                Erste Hilfe Kurse in Bottrop
              </h1>
              <p className="text-base sm:text-lg font-semibold text-slate-700 mt-1">
                Samstags & Sonntags – für Führerschein und Beruf
              </p>
            </div>
            
            {/* DESKTOP: Symmetrisches 3-Spalten Layout - GRÖSSER */}
            {/* [Paket 1]  [VIDEO]  [Paket 3] */}
            {/* [Paket 2]  [VIDEO]  [Paket 4] */}
            <div className="hidden lg:grid lg:grid-cols-4 gap-5 items-stretch">
              
              {/* LINKE SPALTE: Paket 1 & 2 - GRÖSSER */}
              <div className="flex flex-col gap-6">
                {PACKAGES.filter(pkg => !pkg.isBG).slice(0, 2).map((pkg) => {
                  const IconComponent = pkg.icon;
                  return (
                    <Card 
                      key={pkg.id}
                      className={`relative cursor-pointer transition-all duration-200 border-2 hover:shadow-xl group flex-1 ${
                        pkg.popular 
                          ? 'border-red-400 bg-red-50/50 hover:border-red-500' 
                          : 'border-slate-200 hover:border-red-400'
                      }`}
                      onClick={() => navigate(`/termine?paket=${pkg.id}`)}
                      data-testid={`package-left-${pkg.id}`}
                    >
                      {pkg.popular && (
                        <div className="absolute -top-3 left-4 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full z-10">
                          Bestseller
                        </div>
                      )}
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            pkg.popular ? 'bg-red-100' : 'bg-slate-100 group-hover:bg-red-100'
                          }`}>
                            <IconComponent className={`w-7 h-7 ${
                              pkg.popular ? 'text-red-600' : 'text-[#1e3a5f] group-hover:text-red-600'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-[#1e3a5f] leading-tight mb-2">
                              {formatPackageName(pkg.id, pkg.name)}
                            </h3>
                            <p className="text-2xl font-bold text-red-600 mb-2">{pkg.priceLabel}</p>
                            <p className="text-sm text-slate-500 line-clamp-3">{pkg.shortDesc}</p>
                          </div>
                        </div>
                        <Button 
                          className={`w-full mt-5 h-11 text-base ${
                            pkg.popular 
                              ? 'bg-red-600 hover:bg-red-700 text-white' 
                              : 'bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white'
                          }`}
                        >
                          Termin wählen
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {/* MITTE: Video (2 Spalten breit) - NOCH GRÖSSER */}
              <div className="col-span-2 flex items-center justify-center">
                <div 
                  className="relative rounded-2xl overflow-hidden shadow-2xl bg-cover bg-center w-full h-full min-h-[580px]"
                  style={{ 
                    backgroundImage: 'url(/hero_first_frame.jpg)'
                  }}
                  data-testid="hero-video-container"
                >
                  <video
                    ref={desktopVideoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                  >
                    <source src="/hero_video_new.mp4" type="video/mp4" />
                  </video>
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/40 via-transparent to-transparent" />
                  
                  {/* Info auf Video */}
                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-base font-semibold text-[#1e3a5f]">Kursdauer: 7,5 Stunden</p>
                          <p className="text-sm text-slate-500">Sofort gültige Bescheinigung</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">ab</p>
                          <p className="text-3xl font-bold text-red-600">35 €</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* RECHTE SPALTE: Paket 3 & 4 - GRÖSSER */}
              <div className="flex flex-col gap-6">
                {PACKAGES.filter(pkg => !pkg.isBG).slice(2, 4).map((pkg) => {
                  const IconComponent = pkg.icon;
                  return (
                    <Card 
                      key={pkg.id}
                      className={`relative cursor-pointer transition-all duration-200 border-2 hover:shadow-xl group flex-1 ${
                        pkg.popular 
                          ? 'border-red-400 bg-red-50/50 hover:border-red-500' 
                          : 'border-slate-200 hover:border-red-400'
                      }`}
                      onClick={() => navigate(`/termine?paket=${pkg.id}`)}
                      data-testid={`package-right-${pkg.id}`}
                    >
                      {pkg.popular && (
                        <div className="absolute -top-3 left-4 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full z-10">
                          Bestseller
                        </div>
                      )}
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            pkg.popular ? 'bg-red-100' : 'bg-slate-100 group-hover:bg-red-100'
                          }`}>
                            <IconComponent className={`w-7 h-7 ${
                              pkg.popular ? 'text-red-600' : 'text-[#1e3a5f] group-hover:text-red-600'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-[#1e3a5f] leading-tight mb-2">
                              {formatPackageName(pkg.id, pkg.name)}
                            </h3>
                            <p className="text-2xl font-bold text-red-600 mb-2">{pkg.priceLabel}</p>
                            <p className="text-sm text-slate-500 line-clamp-3">{pkg.shortDesc}</p>
                          </div>
                        </div>
                        <Button 
                          className={`w-full mt-5 h-11 text-base ${
                            pkg.popular 
                              ? 'bg-red-600 hover:bg-red-700 text-white' 
                              : 'bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white'
                          }`}
                        >
                          Termin wählen
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
            
            {/* MOBILE: Video oben, dann Pakete */}
            <div className="lg:hidden">
              {/* Video */}
              <div 
                className="relative rounded-xl overflow-hidden shadow-xl bg-cover bg-center mb-6"
                style={{
                  aspectRatio: '16/9',
                  backgroundImage: 'url(/hero_first_frame.jpg)'
                }}
              >
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                >
                  <source src="/hero_video_new.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-[#1e3a5f]">7,5 Stunden Kurs</p>
                        <p className="text-xs text-slate-500">Sofort gültig</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">ab</p>
                        <p className="text-xl font-bold text-red-600">35 €</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Mobile: Pakete als Liste - GRÖSSERE KARTEN wie auf TerminePage */}
              <div className="space-y-4">
                {PACKAGES.filter(pkg => !pkg.isBG).map((pkg) => {
                  const IconComponent = pkg.icon;
                  return (
                    <Card 
                      key={pkg.id}
                      className="relative cursor-pointer transition-all duration-200 border-2 border-slate-200 hover:border-red-300 hover:shadow-md"
                      onClick={() => navigate(`/termine?paket=${pkg.id}`)}
                      data-testid={`package-mobile-${pkg.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          {/* Icon */}
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-slate-100">
                            <IconComponent className="w-6 h-6 text-[#1e3a5f]" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            {/* Paket Name + Preis */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h3 className="font-bold text-[#1e3a5f] text-sm leading-tight flex-1 min-w-0">
                                {formatPackageName(pkg.id, pkg.name)}
                              </h3>
                              <p className="text-lg font-bold text-red-600 whitespace-nowrap flex-shrink-0">
                                {pkg.priceLabel}
                              </p>
                            </div>
                            
                            {/* Kurzbeschreibung */}
                            <p className="text-xs text-slate-600 mb-2 line-clamp-2">
                              {pkg.shortDesc}
                            </p>
                            
                            {/* Details */}
                            <ul className="space-y-0.5">
                              {pkg.details.slice(0, 2).map((detail, index) => (
                                <li key={index} className="flex items-start gap-1.5 text-xs text-slate-500">
                                  <span className="text-red-600">•</span>
                                  <span className="line-clamp-1">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
            
            {/* BG-KURS - Hervorgehoben über volle Breite - GRÖSSER */}
            {PACKAGES.filter(pkg => pkg.isBG).map((pkg) => {
              const IconComponent = pkg.icon;
              return (
                <div 
                  key={pkg.id}
                  className="mt-8 bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8f] rounded-2xl p-5 md:p-8 cursor-pointer hover:shadow-2xl transition-all"
                  onClick={() => navigate(`/termine?paket=${pkg.id}`)}
                  data-testid={`package-bg-${pkg.id}`}
                >
                  {/* Desktop Layout */}
                  <div className="hidden md:flex md:flex-row md:items-center md:justify-between gap-5">
                    <div className="flex items-center gap-5">
                      <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-white">{pkg.name.replace('BOTTROP: ', '')}</h3>
                          <span className="bg-emerald-400 text-emerald-900 text-sm font-semibold px-3 py-1 rounded-full">
                            {pkg.badge}
                          </span>
                        </div>
                        <p className="text-base text-white/80">{pkg.shortDesc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-4xl font-bold text-emerald-400">{pkg.priceLabel}</p>
                        <p className="text-sm text-white/60">für Betriebe mit BG</p>
                      </div>
                      <Button 
                        size="lg"
                        className="bg-white text-[#1e3a5f] hover:bg-slate-100 font-semibold px-8 h-12"
                      >
                        Jetzt anmelden
                      </Button>
                    </div>
                  </div>
                  
                  {/* Mobile Layout - Saubere Ausrichtung */}
                  <div className="md:hidden">
                    {/* Header mit Icon und Badge */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="inline-block bg-emerald-400 text-emerald-900 text-xs font-semibold px-2.5 py-1 rounded-full mb-2">
                          {pkg.badge}
                        </span>
                        <h3 className="text-lg font-bold text-white leading-tight">
                          {pkg.name.replace('BOTTROP: ', '')}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Beschreibung */}
                    <p className="text-sm text-white/80 mb-4">{pkg.shortDesc}</p>
                    
                    {/* Preis */}
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-white/60">Preis für Betriebe mit BG</p>
                      <p className="text-3xl font-bold text-emerald-400">{pkg.priceLabel}</p>
                    </div>
                    
                    {/* Button */}
                    <Button 
                      size="lg"
                      className="w-full bg-white text-[#1e3a5f] hover:bg-slate-100 font-semibold h-12"
                    >
                      Jetzt anmelden
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* HERO-INFOBOX (WAS / WANN / WO) - Kompakt */}
        <section className="py-3">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                
                {/* WAS? - Desktop: linksbündig */}
                <div className="p-6 sm:p-8">
                  <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                    <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                      <svg className="w-7 h-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-[#1e3a5f] mb-3">WAS?</h3>
                    <p className="font-semibold text-slate-800 mb-3">Erste-Hilfe-Kurs (9 Unterrichtseinheiten)</p>
                    <ul className="space-y-2 text-sm text-slate-600 w-full text-left">
                      <li className="flex items-start gap-2 justify-start">
                        <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>Behördlich anerkannt</span>
                      </li>
                      <li className="flex items-start gap-2 justify-start">
                        <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>Für den Führerschein</span>
                      </li>
                      <li className="flex items-start gap-2 justify-start">
                        <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>Für Arbeit, Betrieb, Studium u.v.m.</span>
                      </li>
                      <li className="flex items-start gap-2 justify-start">
                        <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>8 biometrische Passfotos inklusive</span>
                      </li>
                      <li className="flex items-start gap-2 justify-start">
                        <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>Amtlicher Sehtest (Auto & Zweirad)</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* WANN? - Desktop: mittig zentriert */}
                <div className="p-6 sm:p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                      <Clock className="w-7 h-7 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold text-[#1e3a5f] mb-3">WANN?</h3>
                    <div className="space-y-3 text-slate-600">
                      <p className="font-semibold text-slate-800">Jeden Samstag & Sonntag</p>
                      <p className="text-2xl font-bold text-[#1e3a5f]">09:00 – 16:30 Uhr</p>
                      <p className="text-sm">inklusive Pause</p>
                    </div>
                  </div>
                </div>

                {/* WO? - Desktop: rechtsbündig */}
                <div className="p-6 sm:p-8 md:col-span-2 lg:col-span-1">
                  <div className="flex flex-col items-center lg:items-end text-center lg:text-right">
                    <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                      <MapPin className="w-7 h-7 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold text-[#1e3a5f] mb-3">WO?</h3>
                    <div className="space-y-2 text-slate-600">
                      <p className="font-semibold text-slate-800">Erste Hilfe Bottrop</p>
                      <p>Poststraße 6<br />46236 Bottrop</p>
                      <p className="text-sm text-red-600 font-medium mt-3">
                        Super zentral – direkt am ZOB Bottrop
                      </p>
                    </div>
                  </div>
                </div>

              </div>
              
              {/* CTA Button */}
              <div className="bg-slate-50 px-6 py-4 flex justify-center border-t border-slate-100">
                <a href="#paket-auswahl">
                  <Button className="bg-red-600 hover:bg-red-700 text-white px-10 h-12 text-base font-semibold">
                    Paket wählen und Termin buchen
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* VIDEO-SEKTION (2 Videos nebeneinander) - Kompakt */}
        <section className="py-4 bg-white">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Video 1 */}
              <div className="rounded-lg sm:rounded-xl overflow-hidden shadow-lg bg-slate-100">
                <video
                  className="w-full aspect-video"
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center 40%'
                  }}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  poster="/images/hero.png"
                >
                  <source src="/video1.mp4" type="video/mp4" />
                  Ihr Browser unterstützt kein Video.
                </video>
              </div>
              
              {/* Video 2 */}
              <div className="rounded-lg sm:rounded-xl overflow-hidden shadow-lg bg-slate-100">
                <video
                  className="w-full aspect-video"
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center 40%'
                  }}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  poster="/images/hero.png"
                >
                  <source src="/video2.mp4" type="video/mp4" />
                  Ihr Browser unterstützt kein Video.
                </video>
              </div>
            </div>
          </div>
        </section>

        {/* VORBEREITUNG & ORT - Kompakt, zentriert auf Desktop */}
        <section className="py-6 bg-white">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f]">
                Erste-Hilfe-Kurse in Bottrop – alles an einem Ort
              </h2>
            </div>
            
            <div className="max-w-4xl mx-auto text-slate-600 space-y-6 text-center lg:text-lg">
              <p>
                Unsere Erste-Hilfe-Kurse finden in modernen, professionell ausgestatteten Schulungsräumen 
                in der <strong className="text-[#1e3a5f]">Poststraße 6, direkt am ZOB Bottrop</strong>, statt – 
                zentral gelegen und optimal erreichbar.
              </p>
              
              <p>
                Bei uns erwartet dich eine praxisnahe, verständliche Ausbildung, durchgeführt von 
                erfahrenen und anerkannten Ausbildern, die dich gezielt und ohne Stress auf den 
                Ernstfall vorbereiten.
              </p>
              
              <div className="bg-slate-50 rounded-xl p-6 lg:p-8 my-6">
                <h3 className="text-xl lg:text-2xl font-bold text-[#1e3a5f] mb-4 flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-6 h-6 lg:w-7 lg:h-7 text-red-600" />
                  Dein Vorteil bei uns:
                </h3>
                <p className="text-slate-600 mb-0">
                  Du kannst <strong className="text-[#1e3a5f]">biometrische Passfotos</strong> sowie den 
                  <strong className="text-[#1e3a5f]"> amtlichen Sehtest</strong> für deinen Führerschein 
                  direkt vor Ort erledigen.<br /><br />
                  Nach Kursende erhältst du alle erforderlichen Unterlagen, sodass du diese bereits 
                  am nächsten Werktag problemlos bei deiner Fahrschule oder beim Amt einreichen kannst.
                </p>
              </div>
            </div>
            
            <div className="text-center mt-4">
              <a href="#paket-auswahl">
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white px-10 h-14 text-lg font-semibold"
                >
                  Paket wählen und Termin buchen
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* GÜLTIG FÜR - Größer auf Desktop */}
        <section className="py-4">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-[#1e3a5f] mb-3 text-center">
              Gültig für Fahrschüler und Fahrschulen aller Klassen
            </h2>
            <p className="text-base lg:text-xl text-slate-600 text-center mb-6">
              Sowie für viele weitere Bereiche:
            </p>
              
            <div className="max-w-3xl mx-auto">
                <div className="grid sm:grid-cols-2 gap-x-12 gap-y-4">
                  {[
                    "Betriebe",
                    "Auszubildende",
                    "Studierende",
                    "Schüler und Schülerinnen",
                    "Arbeitnehmende",
                    "Ersthelfer und Ersthelferinnen",
                    "Trainer und Trainerinnen",
                    "Sportübungsleiter und Sportübungsleiterinnen",
                    "Betreuende",
                    "MPU"
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3 py-1">
                      <span className="text-red-600 font-medium text-xl">→</span>
                      <span className="text-slate-700 lg:text-xl">{item}</span>
                    </div>
                  ))}
                </div>
            </div>
          </div>
        </section>

        {/* FRAGEN & ANTWORTEN (FAQ) - Größer auf Desktop */}
        <section className="py-6">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-[#1e3a5f] mb-3">
                Fragen & Antworten
              </h2>
              <p className="text-slate-600 lg:text-xl">
                Alles Wichtige rund um unsere Erste-Hilfe-Kurse auf einen Blick.
              </p>
            </div>
              
              <div className="max-w-4xl mx-auto">
                {loading ? (
                  <div className="text-center py-8 text-slate-500">Laden...</div>
                ) : (
                  <Accordion type="single" collapsible className="space-y-4">
                    {faqs.map((faq, index) => (
                      <AccordionItem 
                        key={faq.id} 
                        value={`faq-${faq.id}`} 
                        className="bg-white rounded-lg border-0 px-6 lg:px-8 shadow-sm"
                      >
                        <AccordionTrigger className="text-left text-[#1e3a5f] font-semibold hover:no-underline py-5 lg:py-6 text-base lg:text-xl">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-slate-600 pb-5 lg:pb-6 whitespace-pre-line text-base lg:text-lg">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </div>

            {/* CTA Button */}
            <div className="flex justify-center mt-6">
              <a href="#paket-auswahl">
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white px-10 h-14 text-lg font-semibold"
                >
                  Paket wählen und Termin buchen
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* KUNDENSTIMMEN - SCROLLENDE REZENSIONEN - Volle Breite */}
        <section className="py-6 overflow-hidden">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-2">
                Das sagen unsere Teilnehmer
              </h2>
              <p className="text-slate-600 lg:text-xl">
                Echte Bewertungen von echten Kursteilnehmern.
              </p>
            </div>
            
            {/* Infinite Marquee Container */}
            {loading ? (
              <div className="text-center py-8 text-slate-500">Laden...</div>
            ) : reviews.length > 0 ? (
              <div className="relative overflow-hidden">
                <div className="animate-scroll" style={{ gap: '16px' }}>
                  {/* Erstes Set - Alle Rezensionen */}
                  {reviews.map((review) => (
                    <div 
                      key={review.id}
                      className="flex-shrink-0 w-64 sm:w-80 lg:w-[420px] bg-white rounded-xl shadow-md p-4 sm:p-6 lg:p-8"
                    >
                      <div className="flex gap-1 mb-3 sm:mb-4">
                        {[...Array(review.rating)].map((_, i) => (
                          <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-yellow-400 fill-current" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <p className="text-slate-600 mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base lg:text-lg">
                        „{review.text}"
                      </p>
                      <p className="text-[#1e3a5f] font-semibold text-sm sm:text-base lg:text-lg">
                        – {review.author}
                      </p>
                    </div>
                  ))}
                  {/* Zweites Set - Alle Rezensionen dupliziert für nahtlosen Loop */}
                  {reviews.map((review) => (
                    <div 
                      key={`dup-${review.id}`}
                      className="flex-shrink-0 w-64 sm:w-80 lg:w-[420px] bg-white rounded-xl shadow-md p-4 sm:p-6 lg:p-8"
                    >
                      <div className="flex gap-1 mb-3 sm:mb-4">
                        {[...Array(review.rating)].map((_, i) => (
                          <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-yellow-400 fill-current" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <p className="text-slate-600 mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base lg:text-lg">
                        „{review.text}"
                      </p>
                      <p className="text-[#1e3a5f] font-semibold text-sm sm:text-base lg:text-lg">
                        – {review.author}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Legal Notice */}
            <p className="text-center text-slate-400 text-xs mt-6">
              * Die dargestellten Bewertungen stammen von echten Kursteilnehmern. 
              Namen wurden aus Datenschutzgründen gekürzt.
            </p>
          </div>
        </section>

        {/* KONTAKT - Größer auf Desktop */}
        <section className="py-6">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-[#1e3a5f] mb-8 text-center">
              Kontakt & Standort
            </h2>
              
            <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {/* Adresse - Desktop: größer und gleichwertig */}
              <Card className="border-0 shadow-md bg-white">
                <CardContent className="p-8 lg:p-10">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 lg:w-20 lg:h-20 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-8 h-8 lg:w-10 lg:h-10 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1e3a5f] text-xl lg:text-2xl mb-3">Adresse</h3>
                      <p className="text-slate-600 text-lg lg:text-xl leading-relaxed">
                        Erste Hilfe Bottrop<br />
                        Poststraße 6<br />
                        46236 Bottrop
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Kontakt - Desktop: größer und gleichwertig */}
              <Card className="border-0 shadow-md bg-white">
                <CardContent className="p-8 lg:p-10">
                  <div className="space-y-5 lg:space-y-6">
                    {/* Festnetz */}
                    <div className="flex items-center gap-5 lg:gap-6">
                      <div className="w-14 h-14 lg:w-16 lg:h-16 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="w-7 h-7 lg:w-8 lg:h-8 text-red-600" />
                      </div>
                      <div>
                        <a href="tel:020419878666" className="text-red-600 hover:underline text-lg lg:text-xl font-semibold">02041 9878 666</a>
                      </div>
                    </div>
                    
                    {/* Mobil */}
                    <div className="flex items-center gap-5 lg:gap-6">
                      <div className="w-14 h-14 lg:w-16 lg:h-16 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Smartphone className="w-7 h-7 lg:w-8 lg:h-8 text-red-600" />
                      </div>
                      <div>
                        <a href="tel:017680151197" className="text-red-600 hover:underline text-lg lg:text-xl font-semibold">0176 8015 1197</a>
                      </div>
                    </div>
                    
                    {/* E-Mail */}
                    <div className="flex items-center gap-5 lg:gap-6">
                      <div className="w-14 h-14 lg:w-16 lg:h-16 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="w-7 h-7 lg:w-8 lg:h-8 text-red-600" />
                      </div>
                      <div>
                        <a href="mailto:info@erstehilfebottrop.de" className="text-red-600 hover:underline text-lg lg:text-xl font-semibold">
                          info@erstehilfebottrop.de
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* ANFAHRT - Zentriert mit Icon unter Überschrift */}
        <section className="py-8">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Überschrift zentriert mit Icon darunter */}
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-[#1e3a5f] mb-4">
                Anfahrt
              </h2>
              <div className="w-20 h-20 lg:w-24 lg:h-24 bg-red-100 rounded-2xl flex items-center justify-center mx-auto">
                <Bus className="w-10 h-10 lg:w-12 lg:h-12 text-red-600" />
              </div>
            </div>
              
            {/* Anfahrtsinfo zentriert */}
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-slate-600 space-y-3 lg:text-xl leading-relaxed">
                <p>
                  Ab <strong className="text-[#1e3a5f]">Bottrop Hauptbahnhof</strong> mit dem Bus 186 
                  Richtung Bottrop ZOB Berliner Platz.
                </p>
                <p>
                  Haltestelle <strong className="text-[#1e3a5f]">Untere Hochstraße</strong>.
                </p>
                <p>
                  Fußweg ca. 5 Minuten zur Poststraße 6.
                </p>
                <p>
                  Vom ZOB Berliner Platz ca. 3 Minuten Fußweg.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* STANDORTFOTO - Kompakt */}
        <section className="py-6">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-4">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-2">
                So findest du uns
              </h2>
              <p className="text-slate-600">
                Poststraße 6 · 46236 Bottrop · direkt am ZOB
              </p>
            </div>
              
            {/* Building Photo - Seitenverhältnis des Bildes, volle Breite, keine Ränder */}
            <div className="rounded-xl overflow-hidden shadow-lg mb-3" style={{ aspectRatio: '16/9' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_firstaid-bottrop/artifacts/pandkmj4_BEE47B7C-73DC-46A2-9D8D-55D051EACDF6.jpeg"
                alt="Außenansicht unserer Schulungsräume - Poststraße 6, Bottrop"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-center text-slate-600 text-sm mb-6">
              Unsere Schulungsräume – Poststraße 6, Bottrop (direkt am ZOB)
            </p>
              
            {/* Google Maps Embed */}
            <div className="rounded-xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2484.8789!2d6.9245!3d51.5215!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8e7a8c8c8c8c8%3A0x0!2sPoststra%C3%9Fe%206%2C%2046236%20Bottrop!5e0!3m2!1sde!2sde!4v1704700000000!5m2!1sde!2sde"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Standort Erste Hilfe Bottrop"
                className="w-full h-[250px] sm:h-[300px] lg:h-[400px]"
              />
            </div>
            
            {/* Link zu Google Maps */}
            <div className="text-center mt-4">
              <a 
                href="https://maps.app.goo.gl/CkFcWboHwQijPvyx5?g_st=ic"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-medium transition-colors"
              >
                <MapPin className="w-5 h-5" />
                Route in Google Maps anzeigen
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
